import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import PDFStyles, { ELECTRICAL_COLORS } from '../ElectricalInstallationCertificate/styles/ElectricalInstallationCertificatePDF';
import QRCodeView from '../../utils/electrical/QRCodeView.jsx';
import { transformForPDF } from './transform';

const CircuitTable = ({ circuits }) => {
  const columns = [
    { label: 'No.', width: '6%' },
    { label: 'Description', width: '22%' },
    { label: 'Device', width: '13%' },
    { label: 'Rating', width: '7%' },
    { label: 'Max Zs', width: '8%' },
    { label: 'Measured Zs', width: '9%' },
    { label: 'IR (MΩ)', width: '9%' },
    { label: 'RCD Trip', width: '9%' },
    { label: 'Result', width: '9%' },
    { label: 'Status', width: '8%' }
  ];

  return (
    <View style={PDFStyles.table}>
      {/* Header */}
      <View style={[PDFStyles.tableRow, PDFStyles.tableHeader]}>
        {columns.map(col => (
          <View key={col.label} style={{ width: col.width, ...PDFStyles.tableCell }}>
            <Text style={PDFStyles.tableCell}>{col.label}</Text>
          </View>
        ))}
      </View>

      {/* Rows */}
      {circuits.map((c, i) => {
        const zsStatusColor = c.zsStatus === 'PASS' ? ELECTRICAL_COLORS.green : c.zsStatus === 'FAIL' ? ELECTRICAL_COLORS.red : ELECTRICAL_COLORS.midGray;
        return (
          <View key={i} style={PDFStyles.tableRow}>
            <View style={{ width: '6%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.circuitNumber || i + 1}</Text>
            </View>
            <View style={{ width: '22%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.circuitDescription}</Text>
            </View>
            <View style={{ width: '13%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.protectiveDevice}</Text>
            </View>
            <View style={{ width: '7%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.deviceRating}A</Text>
            </View>
            <View style={{ width: '8%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.maxZs || ''}</Text>
            </View>
            <View style={{ width: '9%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.measuredZs}</Text>
            </View>
            <View style={{ width: '9%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.insulationResistance}</Text>
            </View>
            <View style={{ width: '9%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.rcdTripTime1x ? `${c.rcdTripTime1x}ms` : ''}</Text>
            </View>
            <View style={{ width: '9%', ...PDFStyles.tableCell }}>
              <Text style={PDFStyles.tableCell}>{c.testResult}</Text>
            </View>
            <View style={{ width: '8%', ...PDFStyles.tableCell }}>
              <Text style={[PDFStyles.tableCell, { color: zsStatusColor, fontWeight: 'bold' }]}>
                {c.zsStatus === 'FAIL' ? 'FAIL' : c.zsStatus === 'PASS' ? 'PASS' : ''}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const MinorElectricalWorksPDF = ({ formData }) => {
  const data = transformForPDF(formData);
  const assessmentColor = data.overallAssessment === 'SATISFACTORY' ? ELECTRICAL_COLORS.green : ELECTRICAL_COLORS.red;
  const eb = data.earthingBonding;

  return (
    <Document>
      <Page size="A4" style={PDFStyles.page}>
        {/* Footer */}
        <View style={PDFStyles.footer}>
          <Text>
            Certificate No: {data.worksDescription.certificateNumber}   |   {data.standard}   |   Page
          </Text>
        </View>

        {/* Header with QR */}
        <View style={{ flexDirection: 'row', marginBottom: 8 }}>
          <View style={{ flex: 1 }}>
            <View style={PDFStyles.headerPanel}>
              <Text style={PDFStyles.title}>{data.title}</Text>
              <Text style={PDFStyles.subtitle}>{data.standard}</Text>
              <Text style={PDFStyles.subtitle}>
                Certificate No: {data.worksDescription.certificateNumber} | {data.worksDescription.dateOfWorks}
              </Text>
            </View>
          </View>
          <View style={{ width: 80, alignItems: 'center', paddingLeft: 8 }}>
            <QRCodeView payload={data.verification} moduleSize={2} quietZone={2} />
            <Text style={{ fontSize: 5, textAlign: 'center', color: '#666666', marginTop: 2 }}>Verify</Text>
          </View>
        </View>

        {/* Part 1: Description of minor works */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>Part 1: Description of Minor Works</Text>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Client:</Text>
            <Text style={PDFStyles.fieldValue}>{data.worksDescription.clientName}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Address:</Text>
            <Text style={PDFStyles.fieldValue}>{data.worksDescription.installationAddress}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Description:</Text>
            <Text style={PDFStyles.fieldValue}>{data.worksDescription.descriptionOfWorks}</Text>
          </View>
          {data.worksDescription.existingInstallation && (
            <View style={PDFStyles.fieldRow}>
              <Text style={PDFStyles.fieldLabel}>Existing Install:</Text>
              <Text style={PDFStyles.fieldValue}>{data.worksDescription.existingInstallation}</Text>
            </View>
          )}
        </View>

        {/* Part 2: Earthing and bonding */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>Part 2: Presence and Adequacy of Installation Earthing and Bonding</Text>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Earthing:</Text>
            <Text style={PDFStyles.fieldValue}>{eb.earthingArrangement}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Earth Conductor:</Text>
            <Text style={PDFStyles.fieldValue}>{eb.earthConductor}</Text>
          </View>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Main Bonding:</Text>
            <Text style={PDFStyles.fieldValue}>{eb.mainBonding}</Text>
          </View>
          {eb.supplementaryBonding && (
            <View style={PDFStyles.fieldRow}>
              <Text style={PDFStyles.fieldLabel}>Supplementary:</Text>
              <Text style={PDFStyles.fieldValue}>{eb.supplementaryBonding}</Text>
            </View>
          )}
          {eb.bondingNotes && (
            <View style={PDFStyles.fieldRow}>
              <Text style={PDFStyles.fieldLabel}>Notes:</Text>
              <Text style={PDFStyles.fieldValue}>{eb.bondingNotes}</Text>
            </View>
          )}
        </View>

        {/* Part 3: Circuit details & test results */}
        <View style={PDFStyles.section} wrap={false}>
          <Text style={PDFStyles.sectionTitle}>Part 3: Circuit Details and Test Results</Text>
          <CircuitTable circuits={data.circuits} />
        </View>

        {/* Overall assessment */}
        <View style={[PDFStyles.assessment, { borderColor: assessmentColor }]} wrap={false}>
          <Text style={[PDFStyles.assessmentTitle, { color: assessmentColor }]}>
            Overall Assessment: {data.overallAssessment}
          </Text>
        </View>

        {/* Part 4: Declaration */}
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionTitle}>Part 4: Declaration</Text>
          <Text style={PDFStyles.declaration}>
            I certify that the works described above have been installed, inspected and tested
            in accordance with BS 7671:2018+A2:2022 and the relevant Building Regulations and
            are safe to be connected to the supply.
          </Text>
          <View style={PDFStyles.fieldRow}>
            <Text style={PDFStyles.fieldLabel}>Engineer:</Text>
            <Text style={PDFStyles.fieldValue}>
              {data.declaration.engineerName} (Card: {data.declaration.engineerCardNumber})
            </Text>
          </View>
          {data.declaration.companyName && (
            <View style={PDFStyles.fieldRow}>
              <Text style={PDFStyles.fieldLabel}>Company:</Text>
              <Text style={PDFStyles.fieldValue}>{data.declaration.companyName}</Text>
            </View>
          )}
          {data.declaration.schemeName && (
            <View style={PDFStyles.fieldRow}>
              <Text style={PDFStyles.fieldLabel}>Scheme:</Text>
              <Text style={PDFStyles.fieldValue}>
                {data.declaration.schemeName} ({data.declaration.schemeReference})
              </Text>
            </View>
          )}
        </View>

        {/* Signatures */}
        <View style={PDFStyles.signatureRow}>
          <View style={PDFStyles.signatureBox}>
            <Text>Engineer: {data.declaration.engineerSignature}</Text>
          </View>
          <View style={PDFStyles.signatureBox}>
            <Text>Customer/Client: {data.declaration.customerSignature}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default MinorElectricalWorksPDF;